<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    contentCSS="custom-content-css"
    :title="isEditing ? 'Edit Pipeline' : 'Create New Pipeline'"
  >
    <div
      class="flex flex-col gap-6 text-[13px] secondary_color proxima_nova font-normal"
    >
      <div id="name" class="form_col">
        <span>Pipeline Name</span>
        <a-input
          ref="pipelineNameInputRef"
          v-model:value="formState.name"
          placeholder="Enter Pipeline Name"
          class="input_medium"
          allow-clear
        />
      </div>
      <div class="flex items-center gap-2">
        <a-switch
          :checked="weightedStatsEnabled"
          @change="onToggleWeightedStats"
        />
        <span>Enable probability weightage</span>
      </div>
      <div id="stages" class="form_col">
        <span>Stages</span>
        <VueDraggableNext
          @end="reorderPosition"
          :list="formState.pipeline_stages"
          ghost-class="ghost-card"
          handle=".drag-handle"
        >
          <div
            class="flex gap-2 flex-column space-y-2"
            v-for="(stage, index) in formState.pipeline_stages"
            v-bind:key="index"
          >
            <ThemeImage
              :icon="imageNames.DRAGGABLE_DOTS"
              alt="draggable"
              class="cursor-pointer mt-2 mr-2 drag-handle"
            />
            <a-input
              v-model:value="stage.name"
              placeholder="Enter Stage Name"
              class="input_medium"
              allow-clear
            />
            <a-input-number
              v-if="weightedStatsEnabled"
              v-model:value="stage.weightPercentage"
              :min="0"
              :max="100"
              :precision="2"
              placeholder="Weight %"
              class="w-[250px]"
              :formatter="(value) => formatWeight(value)"
              :parser="(value) => parseWeight(value)"
            />
            <a-select v-if="!weightedStatsEnabled" class="w-[175px]" v-model:value="stage.probability">
              <template #suffixIcon
                ><ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
              /></template>
              <a-select-option
                v-for="probability in probabilities"
                :value="probability.value"
              >
                <div class="flex justify-start items-center">
                  <ThemeImage
                    v-if="probability.icon"
                    class="size-4 mt-0.5"
                    :icon="probability.icon"
                    alt="probability_icon"
                  />
                  <span class="ml-1">{{ probability.text }}</span>
                </div>
              </a-select-option>
            </a-select>
            <a-button
              class="secondary_medium"
              v-if="stage.editable"
              @click="removeStage(index, stage)"
            >
              <ThemeImage :icon="imageNames.CLOSE_GREY" alt="close_icon" class="cursor-pointer" />
            </a-button>
          </div>
        </VueDraggableNext>
        <div class="flex flex-row gap-2 justify-start">
          <a-button class="secondary_medium" @click="addStage">+ Add</a-button>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="onCancel">Cancel</a-button>
        <a-button class="primary_medium" @click="onFinish"
          >{{ isEditing ? "Save Changes" : "Create" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
  <CustomModal
    title="Delete Stage"
    v-model:visible="showDeleteStageModal"
    width="32vw"
  >
    <div class="form_col">
      <span class="text_color"
        >Please select a stage to shift all the deals which are currently under
        {{ toDeleteStageName }}
      </span>

      <span class="text-medium secondary_color mt-5">Stage</span>

      <a-select
        v-model:value="replacementStage"
        placeholder="Select a stage"
        :list-height="190"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
            class="size-4"
          />
        </template>
        <a-select-option
          v-for="stage in stagesMap"
          :key="`delete_${stage.id}_${stage.name}`"
          :value="stage.name"
        >
          {{ stage.name }}
        </a-select-option>
      </a-select>
    </div>
    <template #footer>
      <a-button
        class="primary_medium"
        @click="deleteInUsePipelineStage"
        :disabled="!replacementStage"
      >
        Delete & Save Changes</a-button
      >
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, reactive, onMounted, watch, nextTick } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import CustomDrawer from "@/src/components/CustomDrawer.vue";

import CustomModal from "@/src/components/CustomModal.vue";
import { VueDraggableNext } from "vue-draggable-next";
import { defaultPipelineStages } from "@/src/constants/dealConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  formatWeight,
  parseWeight,
  getProbabilityFromWeight,
  validateWeights,
} from "@/src/utils/pipelineUtils.js";

const props = defineProps({
  pipelineId: { type: Number, default: null },
  isEditing: { type: Boolean, default: null },
  drawerVisible: { type: Boolean, required: true },
  showWeightedStats: { type: Boolean, default: false },
});

const emit = defineEmits(["cancel", "pipeline-saved", "update:modelValue", "weighted-stats-toggled"]);
const internalDrawerVisible = ref(props.drawerVisible);
const weightedStatsEnabled = ref(props.showWeightedStats);
const probabilities = [
  { value: "OPEN", text: "Open", icon: "" },
  { value: "WON", text: "Won", icon: imageNames.TICK_MARK },
  { value: "LOST", text: "Lost", icon: imageNames.CROSS_MARK },
  { value: "PAUSED", text: "Paused", icon: imageNames.PAUSE_VARIANT1_ICON },
];
const formState = reactive({
  name: "",
  pipeline_stages: [...defaultPipelineStages],
});
const showDeleteStageModal = ref(false);
const toDeleteStageName = ref("");
const toDeleteStage = ref(0);
const replacementStage = ref(null);
const stagesMap = reactive([]);
const pipelineNameInputRef = ref(null);

const fetchPipelineDetails = async () => {
  if (!props.pipelineId) return;
  try {
    const response = await DashboardService.fetchPipelineDetails(
      props.pipelineId,
    );
    if (response.success) {
      // Convert weight (float) to percentage for display
      if (response.pipeline.pipeline_stages) {
        response.pipeline.pipeline_stages = response.pipeline.pipeline_stages.map(stage => ({
          ...stage,
          weightPercentage: stage.weight != null ? parseFloat((stage.weight * 100).toFixed(2)) : null
        }));
      }
      Object.assign(formState, response.pipeline);
      applyEditableFlag();
      resetStagesMap();
    } else {
      message.error("Failed to fetch pipeline details");
    }
  } catch (error) {
    console.error("Error fetching pipeline details:", error);
    message.error("An error occurred while fetching pipeline details");
  }
};

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      emit("update:modelValue", false);
    } else {
      nextTick(() => {
        if (pipelineNameInputRef.value) {
          pipelineNameInputRef.value?.focus();
        }
      });
    }
  },
);

watch(
  () => props.showWeightedStats,
  (newVal) => {
    weightedStatsEnabled.value = newVal;
  },
);

const onToggleWeightedStats = async (checked) => {
  try {
    const response = await DashboardService.toggleWeightedStats(checked);
    if (response.success) {
      weightedStatsEnabled.value = response.show_weighted_stats;
      emit("weighted-stats-toggled", response.show_weighted_stats);
    } else {
      message.error("Failed to update weighted stats setting");
    }
  } catch (error) {
    console.error("Error toggling weighted stats:", error);
    message.error("An error occurred while updating the setting");
  }
};

const addStage = () => {
  formState.pipeline_stages.push({
    name: "",
    order_index: formState.pipeline_stages.length,
    probability: "OPEN",
    weightPercentage: 100,
    editable: true,
  });
};

const removeStage = async (indexToRemove, stage) => {
  if (stage.id === undefined) {
    formState.pipeline_stages.splice(indexToRemove, 1);
    formState.pipeline_stages.forEach((stage, index) => {
      stage.order_index = index;
    });
    resetStagesMap();
    return;
  }
  resetStagesMap();
  try {
    let response;
    response = await DashboardService.checkPipelineStage(stage.id);
    if (response.success) {
      formState.pipeline_stages.splice(indexToRemove, 1);
      formState.pipeline_stages.forEach((stage, index) => {
        stage.order_index = index;
      });
      resetStagesMap();
    } else if (response.in_use) {
      stagesMap.splice(indexToRemove, 1);
      showDeleteStageModal.value = true;
      replacementStage.value = null;
      toDeleteStageName.value = stage.name;
      toDeleteStage.value = stage.id;
    } else {
      message.error(response.error_message);
    }
  } catch (error) {
    console.error("Error removing pipeline stage");
    message.error("Error removing pipeline stage");
  }
};

const reorderPosition = () => {
  formState.pipeline_stages.forEach((stage, index) => {
    stage.order_index = index + 1;
  });

  applyEditableFlag();
};

const applyEditableFlag = () => {
  formState.pipeline_stages.forEach((stage, index) => {
    stage.editable = index !== 0;
  });
};

const onCancel = () => {
  resetFormState();
  emit("update:modelValue", false); // Close the drawer
};

const resetFormState = () => {
  Object.assign(formState, {
    name: "",
    pipeline_stages: [...defaultPipelineStages],
  });
  resetStagesMap();
};

const resetStagesMap = () => {
  stagesMap.splice(0, stagesMap.length);
  formState.pipeline_stages.forEach((stage) => {
    if (stage.name && stage.name.trim()) {
      stagesMap.push({ name: stage.name, id: stage.id });
    }
  });
};

watch(
  () => props.drawerVisible,
  (newVal) => {
    if (!props.drawerVisible) {
      resetFormState();
    }
    internalDrawerVisible.value = newVal;
    if (newVal && props.isEditing) {
      fetchPipelineDetails();
    }
  },
);

const validateStageWeights = () => {
  if (!weightedStatsEnabled.value) return true;

  const result = validateWeights(formState.pipeline_stages);
  if (!result.valid) {
    message.error(result.error);
    return false;
  }
  return true;
};

const prepareDataForSubmit = () => {
  // Convert weightPercentage to weight (float) for API and exclude weightPercentage
  const pipelineStages = formState.pipeline_stages.map(({ weightPercentage, ...stage }) => ({
    ...stage,
    probability: weightedStatsEnabled.value
      ? getProbabilityFromWeight(weightPercentage)
      : stage.probability,
    weight: weightPercentage != null && weightPercentage !== ""
      ? parseFloat(weightPercentage) / 100
      : null
  }));

  return {
    ...formState,
    pipeline_stages: pipelineStages
  };
};

const onFinish = async () => {
  // Validate weights before submitting
  if (!validateStageWeights()) {
    return;
  }

  try {
    let response;
    const dataToSubmit = prepareDataForSubmit();

    if (props.isEditing) {
      response = await DashboardService.updatePipeline(
        props.pipelineId,
        dataToSubmit,
      );
    } else {
      response = await DashboardService.createPipeline(dataToSubmit);
    }

    if (response.success) {
      message.success(
        `Pipeline ${props.isEditing ? "updated" : "added"} successfully`,
      );
      emit("pipeline-saved", true);
      emit("update:modelValue", false);
    } else {
      message.error(`Failed to ${props.isEditing ? "update" : "add"} pipeline`);
    }
  } catch (error) {
    console.error(
      `Error ${props.isEditing ? "updat" : "add"}ing pipeline:`,
      error,
    );
    message.error(
      `An error occurred while ${props.isEditing ? "updat" : "add"}ing the pipeline`,
    );
  }
  resetFormState();
};

const deleteInUsePipelineStage = async () => {
  let key = 0;
  try {
    const dataToSubmit = prepareDataForSubmit();
    let updateResponse = await DashboardService.updatePipeline(
      props.pipelineId,
      dataToSubmit,
    );
    if (updateResponse.success) {
      message.success(
        `Pipeline ${props.isEditing ? "updated" : "added"} successfully`,
      );
      await fetchPipelineDetails();
      key = stagesMap.find((stage) => stage.name === replacementStage.value);
      emit("pipeline-saved", true);
      emit("update:model-value", false);
      resetFormState();
    }
    let response;
    response = await DashboardService.deletePipelineStage(
      toDeleteStage.value,
      key.id,
    );
    if (response.success) {
      message.success(
        `${toDeleteStageName.value} stage has been successfully deleted`,
      );
    } else {
      message.error("Error deleting pipeline stage");
    }
  } catch (error) {
    console.error("Error deleting pipeline stage");
    message.error("Error deleting pipeline stage");
  } finally {
    showDeleteStageModal.value = false;
    toDeleteStage.value = 0;
    toDeleteStageName.value = "";
    replacementStage.value = null;
  }
};

onMounted(() => {
  fetchPipelineDetails();
});
</script>
